import type { Editor } from '@tiptap/react';
import { useCallback, useEffect, useRef, useState } from 'react';

export type NormalizedVersionSelection = string[] | null;

export function versionSelectionKey(value: NormalizedVersionSelection): string {
    if (value === null) {
        return '';
    }
    return JSON.stringify(value);
}

export function versionSelectionsEqual(a: NormalizedVersionSelection, b: NormalizedVersionSelection): boolean {
    return versionSelectionKey(a) === versionSelectionKey(b);
}

export function computeNextVersionSelection(
    current: NormalizedVersionSelection,
    toggledVersion: string,
    allVersions: string[],
): NormalizedVersionSelection {
    if (current === null) {
        return allVersions.filter(version => version !== toggledVersion).sort();
    }

    const versionSet = new Set(current);
    if (versionSet.has(toggledVersion)) {
        versionSet.delete(toggledVersion);
    } else {
        versionSet.add(toggledVersion);
    }
    const next = [...versionSet].sort();
    if (next.length === allVersions.length && allVersions.every(version => versionSet.has(version))) {
        return null;
    }
    return next;
}

export function useDeferredVersionFilter(params: {
    editor: Editor | null;
    appliedSelection: NormalizedVersionSelection;
    allVersions: string[];
    onLoadingChange: (loading: boolean) => void;
}): {
    pendingSelection: NormalizedVersionSelection;
    displaySelection: NormalizedVersionSelection;
    popoverOpen: boolean;
    handlePopoverOpenChange: (open: boolean) => void;
    applyPending: () => void;
    cancelPending: () => void;
    toggleVersion: (version: string) => void;
    showAllVersions: () => void;
    clearAllVersions: () => void;
    hasPendingChanges: boolean;
    isVersionChecked: (version: string) => boolean;
} {
    const { editor, appliedSelection, allVersions, onLoadingChange } = params;

    const [popoverOpen, setPopoverOpen] = useState(false);
    const [draftSelection, setDraftSelection] = useState<NormalizedVersionSelection>(appliedSelection);
    const pendingSelection = popoverOpen ? draftSelection : appliedSelection;

    const appliedRef = useRef(appliedSelection);
    const pendingRef = useRef(pendingSelection);
    const onLoadingChangeRef = useRef(onLoadingChange);

    useEffect(() => {
        appliedRef.current = appliedSelection;
        pendingRef.current = pendingSelection;
        onLoadingChangeRef.current = onLoadingChange;
    }, [appliedSelection, pendingSelection, onLoadingChange]);

    const snapshotRef = useRef<NormalizedVersionSelection>(appliedSelection);
    /** True when Apply already committed; close handlers should not discard. */
    const committedOnCloseRef = useRef(false);

    const commitPending = useCallback(() => {
        const pending = pendingRef.current;
        const applied = appliedRef.current;

        if (versionSelectionsEqual(pending, applied)) {
            return;
        }

        if (!editor) {
            return;
        }

        onLoadingChangeRef.current(true);
        editor.commands.setVersionFilter(pending);
        queueMicrotask(() => onLoadingChangeRef.current(false));
    }, [editor]);

    const updatePending = useCallback((next: NormalizedVersionSelection) => {
        pendingRef.current = next;
        setDraftSelection(next);
    }, []);

    const revertDraftToSnapshot = useCallback(() => {
        updatePending(snapshotRef.current);
    }, [updatePending]);

    const handlePopoverOpenChange = useCallback(
        (open: boolean) => {
            setPopoverOpen(open);
            if (open) {
                snapshotRef.current = appliedRef.current;
                setDraftSelection(appliedRef.current);
                pendingRef.current = appliedRef.current;
                committedOnCloseRef.current = false;
                return;
            }
            if (committedOnCloseRef.current) {
                committedOnCloseRef.current = false;
                return;
            }
            // Escape / click-away: discard like Cancel — only Apply commits.
            revertDraftToSnapshot();
        },
        [revertDraftToSnapshot],
    );

    const applyPending = useCallback(() => {
        commitPending();
        committedOnCloseRef.current = true;
        setPopoverOpen(false);
    }, [commitPending]);

    const cancelPending = useCallback(() => {
        revertDraftToSnapshot();
        committedOnCloseRef.current = false;
        setPopoverOpen(false);
    }, [revertDraftToSnapshot]);

    const toggleVersion = useCallback(
        (toggledVersion: string) => {
            updatePending(computeNextVersionSelection(pendingRef.current, toggledVersion, allVersions));
        },
        [updatePending, allVersions],
    );

    const showAllVersions = useCallback(() => {
        updatePending(null);
    }, [updatePending]);

    const clearAllVersions = useCallback(() => {
        updatePending([]);
    }, [updatePending]);

    const hasPendingChanges = popoverOpen && !versionSelectionsEqual(draftSelection, appliedSelection);

    const isVersionChecked = useCallback(
        (version: string) => {
            const selection = popoverOpen ? pendingSelection : appliedSelection;
            return selection === null || selection.includes(version);
        },
        [popoverOpen, pendingSelection, appliedSelection],
    );

    const displaySelection = popoverOpen ? pendingSelection : appliedSelection;

    return {
        pendingSelection,
        displaySelection,
        popoverOpen,
        handlePopoverOpenChange,
        applyPending,
        cancelPending,
        toggleVersion,
        showAllVersions,
        clearAllVersions,
        hasPendingChanges,
        isVersionChecked,
    };
}
